import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, ArrowRight, Star } from "@phosphor-icons/react";
import { API_BASE, api } from "@/lib/api";
import { fmtClock, fmtLatency, makeSpeakerColor } from "@/lib/format";
import { BoardSections } from "@/components/observer/Board";
import { btnGhost, btnPrimary } from "@/lib/ui";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between py-2.5 border-b border-[var(--o-border-soft)] text-sm">
    <span className="text-[var(--o-muted2)]">{k}</span>
    <span className="font-mono" data-testid={testid}>{v}</span>
  </div>
);

// Who spoke, as a share of words transcribed. Colours follow the transcript: handed out in order of first appearance.
const TalkTime = ({ talk, participants }) => {
  if (!talk?.speakers?.length) return null;
  const colorOf = makeSpeakerColor(participants);
  [...talk.speakers].sort((a, b) => a.first_t - b.first_t).forEach((s) => colorOf(s.name));
  const mostlyUnnamed = talk.unattributed_share > 0.5;
  return (
    <div className="mt-10" data-testid="summary-talk-time">
      <h2 className="font-display text-base md:text-lg font-bold">Who spoke</h2>
      <p className="text-xs text-[var(--o-muted2)] mt-1">Share of words transcribed, not airtime. Names come from automatic transcription and can be wrong.</p>
      {mostlyUnnamed && (
        <p className="text-xs text-[var(--o-warn)] mt-3 border-l border-[var(--o-warn)] pl-3" data-testid="summary-talk-time-unnamed">
          {Math.round(talk.unattributed_share * 100)}% of the words had no speaker name, so these shares are not reliable. List the participants before the meeting, or try the Scribe transcription engine.
        </p>
      )}
      <ul className="mt-4 space-y-3">
        {talk.speakers.map((s) => {
          const pct = Math.round(s.share * 100);
          const color = s.attributed ? colorOf(s.name) : "var(--o-muted2)";
          return (
            <li key={s.name} data-testid="summary-talk-row">
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="font-semibold truncate" style={{ color }}>{s.name}</span>
                <span className="font-mono text-xs text-[var(--o-muted)] whitespace-nowrap">{pct < 1 && s.share > 0 ? "<1" : pct}% · {s.words.toLocaleString()} words</span>
              </div>
              <div className="h-1.5 mt-1.5 bg-[var(--o-border)]" aria-hidden="true">
                <div className="h-full" style={{ width: `${Math.max(s.share * 100, 1)}%`, background: color }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// The meeting in chapters, as written while it ran (the last one when it ended).
const Chapters = ({ chapters }) => {
  if (!chapters?.length) return null;
  return (
    <div className="mt-10" data-testid="summary-chapters">
      <h2 className="font-display text-base md:text-lg font-bold">Chapters</h2>
      <p className="text-xs text-[var(--o-muted2)] mt-1">Written by the AI every few minutes from the transcript. Check anything important against it.</p>
      <ol className="mt-4 space-y-5">
        {chapters.map((c) => (
          <li key={c.n} className="border-l-2 border-[var(--o-border-strong)] pl-4" data-testid="summary-chapter">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--o-muted3)]">{c.n} · {fmtClock(c.start)}–{fmtClock(c.end)}</div>
            <div className="font-semibold mt-0.5">{c.title}</div>
            {c.summary && <p className="text-sm text-[var(--o-muted)] mt-1 leading-relaxed">{c.summary}</p>}
            {c.points?.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {c.points.map((pt, i) => <li key={i} className="text-xs text-[var(--o-muted2)]">{pt}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

// After a saved meeting ends, one pass over the whole recording relabels the voices (backend speaker_pass.py).
// The summary waits for it and refreshes "Who spoke" when it lands.
function useSpeakerPass(id, initial, enabled) {
  const [stats, setStats] = useState(initial);
  useEffect(() => setStats(initial), [initial]);
  const status = stats?.speaker_pass?.status;
  // Stats that arrived empty (an End that raced another) are fetched from the saved log once.
  const empty = !!stats && stats.transcript_seconds === undefined;
  useEffect(() => {
    if (!enabled || !empty) return;
    api.get(`/sessions/${id}/record`).then(({ data }) => data?.stats?.transcript_seconds !== undefined && setStats(data.stats)).catch(() => {});
  }, [id, enabled, empty]);
  useEffect(() => {
    if (!enabled || !["pending", "running"].includes(status)) return undefined;
    const iv = setInterval(() => {
      api.get(`/sessions/${id}/record`).then(({ data }) => data?.stats && setStats(data.stats)).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, [id, status, enabled]);
  return stats;
}

const PassNote = ({ sp }) => {
  if (!sp) return null;
  const text = {
    pending: "Rechecking voices across the whole meeting. This usually takes under a minute per hour of audio; this page updates by itself.",
    running: "Rechecking voices across the whole meeting. This usually takes under a minute per hour of audio; this page updates by itself.",
    done: `Voices rechecked across the whole meeting: ${sp.voices} voice${sp.voices === 1 ? "" : "s"}, ${sp.named} matched to a name. The saved transcript uses these labels.`,
    failed: `The whole-meeting voice check didn't finish (${sp.error || "unknown error"}). These are the live labels.`,
    skipped: null,
  }[sp.status];
  if (!text) return null;
  return (
    <p className={`text-xs mt-3 border-l pl-3 ${sp.status === "failed" ? "text-[var(--o-warn)] border-[var(--o-warn)]" : "text-[var(--o-muted)] border-[var(--o-border-strong)]"}`} data-testid="summary-speaker-pass">
      {text}
    </p>
  );
};

// isPrivate: true = a private meeting, false = a saved one, undefined = we no longer know (the page was reloaded after it ended).
export const SessionSummary = ({ id, stats: initialStats, title, participants = [], isPrivate }) => {
  const stats = useSpeakerPass(id, initialStats, isPrivate === false);
  return (
  <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)] px-6 sm:px-12 py-16" data-testid="session-summary">
    <div className="relative z-10 max-w-3xl rise">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--o-muted2)]">{isPrivate ? "Private session closed · nothing was saved" : isPrivate === false ? "Session closed · log written" : "Session no longer running"}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">{title || "Session ended"}</h1>
      {isPrivate ? (
        <p className="text-sm text-[var(--o-warn)] mt-4 max-w-xl border-l border-[var(--o-warn)] pl-3" data-testid="summary-private-note">
          This meeting was private. The transcript, answers and log were wiped from the server and can&rsquo;t be recovered. This screen is the only record, and it disappears when you leave the page. The AI services you use still received the audio and text.
        </p>
      ) : isPrivate === false ? (
        <p className="font-mono text-xs text-[var(--o-muted3)] mt-4">backend/sessions/{id}.json</p>
      ) : (
        <p className="text-sm text-[var(--o-muted)] mt-4 max-w-xl border-l border-[var(--o-border-strong)] pl-3" data-testid="summary-unknown-note">
          If this was a private meeting it has been wiped and can&rsquo;t be recovered. Any other meeting was saved as <span className="font-mono">{id}.json</span>.
        </p>
      )}
      {stats && (
        <div className="grid sm:grid-cols-2 gap-x-10 mt-10">
          <div>
            <Row k="Transcript" v={fmtClock(stats.transcript_seconds)} />
            <Row k="Answers (shown)" v={`${stats.answers} (${stats.answers_shown})`} testid="summary-answers" />
            <Row k="Median latency" v={fmtLatency(stats.median_answer_latency_ms)} />
            <Row k="Est. cost" v={`$${stats.estimated_cost_usd}`} />
            {stats.cost_breakdown_usd && (
              <Row k="· transcription / answers / flags" v={`$${stats.cost_breakdown_usd.transcription} / $${stats.cost_breakdown_usd.answers} / $${stats.cost_breakdown_usd.flag_checks}`} testid="summary-cost-breakdown" />
            )}
          </div>
          <div>
            <Row k="Chunks / failed" v={`${stats.chunks} / ${stats.chunk_failures}`} testid="summary-chunks" />
            {stats.chunks_silent > 0 && <Row k="Silent, not transcribed" v={stats.chunks_silent} testid="summary-silent" />}
            <Row k="Chunk failure rate" v={`${(stats.chunk_failure_rate * 100).toFixed(1)}%`} />
            {stats.chunk_fallbacks > 0 && <Row k="Rescued by Sarvam" v={stats.chunk_fallbacks} testid="summary-fallbacks" />}
            <Row k="Flags shown / clicked" v={`${stats.flags_shown} / ${stats.flags_clicked}`} />
            <Row k="Flags ignored" v={stats.flags_ignored} />
          </div>
        </div>
      )}
      {stats?.board?.length > 0 && (
        <div className="mt-10" data-testid="summary-board">
          <h2 className="font-display text-base md:text-lg font-bold">Discussion board</h2>
          <p className="text-xs text-[var(--o-muted2)] mt-1 mb-4">Where the conversation ended up, as the AI read it. Check anything important against the transcript.</p>
          <BoardSections items={stats.board} compact />
        </div>
      )}
      {stats && <Chapters chapters={stats.chapters} />}
      {stats && <TalkTime talk={stats.talk_time} participants={participants} />}
      {stats && <PassNote sp={stats.speaker_pass} />}
      {stats?.missing_data?.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-base md:text-lg font-bold">Answered badly for lack of data</h2>
          <ul className="mt-3 space-y-2">
            {stats.missing_data.map((m) => (
              <li key={m.n} className="text-sm text-[var(--o-muted)] border-l border-[var(--o-border-strong)] pl-3"><span className="font-mono text-[var(--o-fg)]">#{m.n}</span> {m.missing}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-12">
        {isPrivate !== true && <a href={`${API_BASE}/sessions/${id}/download`} className={btnGhost} data-testid="summary-download-log"><DownloadSimple size={15} /> Download log</a>}
        {isPrivate !== true && <Link to={`/score/${id}`} className={btnGhost} data-testid="summary-score-link"><Star size={15} /> Score this session</Link>}
        <Link to="/" className={btnPrimary} data-testid="summary-new-session">New session <ArrowRight size={15} weight="bold" /></Link>
      </div>
    </div>
  </div>
);
};
